import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Lock, Pencil, Send, Users, Search, RefreshCw, ChevronDown, ChevronRight,
  Bold, Italic, List, Link2, Megaphone, CheckCircle2, Eye, Plus, Trash2, Zap, UserCog,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_ORDER = ["systeme", "membre", "pro", "organisme", "asso", "club", "admin"];
const MANUAL_AUDIENCES = [
  { value: "membre", label: "Membres" },
  { value: "pro", label: "Partenaires / Pros" },
  { value: "organisme", label: "Organismes & CSE" },
  { value: "association", label: "Associations" },
  { value: "club", label: "Clubs" },
];
const PREVIEW_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin/emails/preview/";

/* ------------------------------------------------------------------ */
/* Aperçu fidèle (iframe du modèle réel capturé à l'envoi)             */
/* ------------------------------------------------------------------ */
function PreviewDialog({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="email-preview-dialog">
        <DialogHeader><DialogTitle>Aperçu — {item?.label}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2 mb-2">
          Modèle réel du dernier envoi. Si rien ne s'affiche, cet e-mail n'a pas encore été envoyé.
        </div>
        {item && (
          <iframe
            title="aperçu e-mail"
            src={PREVIEW_BASE + encodeURIComponent(item.key)}
            className="w-full h-[60vh] border border-slate-200 rounded-lg bg-white"
            data-testid="email-preview-frame"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Onglet « E-mails automatiques »                                     */
/* ------------------------------------------------------------------ */
function AutoEmails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState({ systeme: true, membre: true });
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [subj, setSubj] = useState("");
  const [intro, setIntro] = useState("");
  const [freq, setFreq] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/catalog");
      setItems(data.items || []);
    } catch { toast.error("Erreur de chargement du catalogue."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (key, body) => (await api.put(`/admin/emails/config/${key}`, body)).data;

  const toggle = async (it, next) => {
    setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, active: next } : x)));
    try {
      await persist(it.key, { active: next, subject_override: it.subject_override, intro_override: it.intro_override, frequency_override: it.frequency_override });
      toast.success(next ? "E-mail activé." : "E-mail désactivé.");
    } catch {
      toast.error("Échec de la mise à jour.");
      setItems((prev) => prev.map((x) => (x.key === it.key ? { ...x, active: !next } : x)));
    }
  };

  const openEdit = (it) => { setEditing(it); setSubj(it.subject_override || ""); setIntro(it.intro_override || ""); setFreq(it.frequency_override || ""); };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await persist(editing.key, { active: editing.active, subject_override: subj, intro_override: intro, frequency_override: freq });
      setItems((prev) => prev.map((x) => (x.key === editing.key ? { ...x, subject_override: subj.trim(), intro_override: intro.trim(), frequency_override: freq.trim() } : x)));
      toast.success("Personnalisation enregistrée.");
      setEditing(null);
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const groups = AUDIENCE_ORDER
    .map((aud) => ({ aud, label: (items.find((i) => i.audience === aud) || {}).audience_label || aud, list: items.filter((i) => i.audience === aud) }))
    .filter((g) => g.list.length);

  if (loading) return <div className="text-slate-500 text-sm py-10" data-testid="admin-emails-loading">Chargement…</div>;

  return (
    <div data-testid="admin-emails-auto">
      <p className="text-slate-500 text-sm mb-4">
        Visualisez le <strong>modèle exact</strong> de chaque e-mail (bouton Aperçu), activez/coupez-le, et personnalisez son
        <strong> objet</strong>, son <strong>message d'intro</strong> et la <strong>fréquence affichée</strong>.
        Les e-mails système restent toujours actifs.
      </p>
      <div className="space-y-4">
        {groups.map((g) => {
          const open = openGroups[g.aud];
          return (
            <div key={g.aud} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`email-group-${g.aud}`}>
              <button
                onClick={() => setOpenGroups((p) => ({ ...p, [g.aud]: !p[g.aud] }))}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100"
                data-testid={`email-group-toggle-${g.aud}`}
              >
                <span className="font-semibold text-slate-800 flex items-center gap-2">
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />} {g.label}
                  <span className="text-xs font-normal text-slate-400">({g.list.length})</span>
                </span>
              </button>
              {open && (
                <div className="divide-y divide-slate-100">
                  {g.list.map((it) => (
                    <div key={it.key} className="px-4 py-3 flex flex-wrap items-start gap-3" data-testid={`email-row-${it.key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-sm">{it.label}</span>
                          <Badge variant="outline" className="text-[10px] font-normal">{it.frequency_override || it.frequency}</Badge>
                          {it.critical && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                              <Lock size={10} /> Toujours actif
                            </span>
                          )}
                          {(it.subject_override || it.intro_override || it.frequency_override) && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
                              <CheckCircle2 size={10} /> Personnalisé
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => setPreview(it)} data-testid={`email-preview-${it.key}`}>
                          <Eye size={13} className="mr-1" /> Aperçu
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`email-edit-${it.key}`}>
                          <Pencil size={13} className="mr-1" /> Personnaliser
                        </Button>
                        <Switch checked={it.active} disabled={it.critical} onCheckedChange={(v) => toggle(it, v)} data-testid={`email-switch-${it.key}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <PreviewDialog item={preview} onClose={() => setPreview(null)} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="email-edit-dialog">
          <DialogHeader><DialogTitle>Personnaliser — {editing?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Objet personnalisé (vide = objet par défaut)</label>
              <Input value={subj} onChange={(e) => setSubj(e.target.value)} placeholder="Objet de l'e-mail…" data-testid="email-edit-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Fréquence affichée</label>
              <Input value={freq} onChange={(e) => setFreq(e.target.value)} placeholder={editing?.frequency || "Ex. Hebdomadaire"} data-testid="email-edit-frequency" />
              <p className="text-[11px] text-slate-400 mt-1">Modifie l'étiquette de fréquence affichée (par défaut : « {editing?.frequency} »).</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Message d'intro (ajouté en haut de l'e-mail)</label>
              <Textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} placeholder="Ex. Petit mot de l'équipe PROXIreduc-28…" data-testid="email-edit-intro" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="email-edit-save">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Éditeur enrichi simple                                              */
/* ------------------------------------------------------------------ */
function RichEditor({ onChange, initialHtml = "" }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.innerHTML = initialHtml || ""; }, []); // eslint-disable-line
  const fire = () => onChange(ref.current?.innerHTML || "");
  const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); fire(); };
  const addLink = () => { const url = window.prompt("Adresse du lien (https://…)"); if (url) { document.execCommand("createLink", false, url); fire(); } };
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 bg-slate-50 border-b border-slate-200 px-2 py-1">
        <button type="button" onClick={() => exec("bold")} className="p-1.5 rounded hover:bg-slate-200" title="Gras" data-testid="rich-bold"><Bold size={14} /></button>
        <button type="button" onClick={() => exec("italic")} className="p-1.5 rounded hover:bg-slate-200" title="Italique" data-testid="rich-italic"><Italic size={14} /></button>
        <button type="button" onClick={() => exec("insertUnorderedList")} className="p-1.5 rounded hover:bg-slate-200" title="Liste" data-testid="rich-list"><List size={14} /></button>
        <button type="button" onClick={addLink} className="p-1.5 rounded hover:bg-slate-200" title="Lien" data-testid="rich-link"><Link2 size={14} /></button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={fire}
        className="min-h-[160px] px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
        data-testid="manual-body-editor"
        suppressContentEditableWarning
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Onglet « E-mails personnalisés » (déclencheurs)                     */
/* ------------------------------------------------------------------ */
const EMPTY_CUSTOM = { name: "", trigger: "", recipient_type: "admins", recipient_email: "", subject: "", body: "", active: true };

function CustomEmails() {
  const [items, setItems] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (nouveau) | item
  const [form, setForm] = useState(EMPTY_CUSTOM);
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/custom");
      setItems(data.items || []);
      setTriggers(data.triggers || []);
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY_CUSTOM, trigger: (triggers[0] || {}).key || "" }); setEditorKey((k) => k + 1); setEditing({}); };
  const openEdit = (it) => { setForm({ name: it.name, trigger: it.trigger, recipient_type: it.recipient_type, recipient_email: it.recipient_email || "", subject: it.subject, body: it.body, active: it.active }); setEditorKey((k) => k + 1); setEditing(it); };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const currentTrigger = triggers.find((t) => t.key === form.trigger);

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error("Donnez un nom."); return; }
    if (!form.trigger) { toast.error("Choisissez un déclencheur."); return; }
    if (form.subject.trim().length < 2) { toast.error("Ajoutez un objet."); return; }
    if (form.body.replace(/<[^>]+>/g, "").trim().length < 3) { toast.error("Ajoutez un message."); return; }
    if (form.recipient_type === "custom" && !/.+@.+\..+/.test(form.recipient_email)) { toast.error("Adresse destinataire invalide."); return; }
    setSaving(true);
    try {
      if (editing && editing.id) await api.put(`/admin/emails/custom/${editing.id}`, form);
      else await api.post("/admin/emails/custom", form);
      toast.success("E-mail automatisé enregistré.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.name} » ?`)) return;
    try { await api.delete(`/admin/emails/custom/${it.id}`); setItems((p) => p.filter((x) => x.id !== it.id)); toast.success("Supprimé."); }
    catch { toast.error("Échec de la suppression."); }
  };

  const toggleActive = async (it, next) => {
    setItems((p) => p.map((x) => (x.id === it.id ? { ...x, active: next } : x)));
    try { await api.put(`/admin/emails/custom/${it.id}`, { ...it, active: next }); }
    catch { toast.error("Échec."); setItems((p) => p.map((x) => (x.id === it.id ? { ...x, active: !next } : x))); }
  };

  const sendTest = async (it) => {
    try { const { data } = await api.post(`/admin/emails/custom/${it.id}/test`); toast.success(`Test envoyé à ${data.to}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec du test."); }
  };

  return (
    <div data-testid="admin-emails-custom">
      <div className="flex items-center justify-between mb-4">
        <p className="text-slate-500 text-sm max-w-2xl">
          Créez des e-mails déclenchés par un événement (ex. <strong>une carte de visite est créée</strong>). Utilisez des variables
          comme <code className="bg-slate-100 px-1 rounded">{"{nom}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{email}"}</code> dans l'objet et le message.
        </p>
        <Button onClick={openNew} className="bg-[#E05638] hover:bg-[#c94a2f] shrink-0" data-testid="custom-new-btn">
          <Plus size={15} className="mr-1" /> Nouvel e-mail
        </Button>
      </div>

      {loading ? <div className="text-slate-400 text-sm py-10">Chargement…</div> :
       items.length === 0 ? (
         <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500" data-testid="custom-empty">
           <Zap className="mx-auto mb-2 text-[#E05638]" /> Aucun e-mail automatisé pour l'instant.
           <div className="mt-3"><Button onClick={openNew} variant="outline" data-testid="custom-empty-new">Créer le premier</Button></div>
         </div>
       ) : (
         <div className="space-y-2">
           {items.map((it) => (
             <div key={it.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`custom-row-${it.id}`}>
               <div className="min-w-0 flex-1">
                 <div className="flex items-center gap-2 flex-wrap">
                   <span className="font-semibold text-slate-900 text-sm">{it.name}</span>
                   <Badge variant="outline" className="text-[10px]"><Zap size={9} className="mr-1" />{it.trigger_label}</Badge>
                   <span className="text-[10px] text-slate-400">
                     → {it.recipient_type === "admins" ? "admins" : it.recipient_type === "actor" ? "la personne concernée" : it.recipient_email}
                   </span>
                 </div>
                 <div className="text-xs text-slate-500 mt-0.5 truncate">Objet : {it.subject}</div>
               </div>
               <div className="flex items-center gap-2 shrink-0">
                 <Button variant="ghost" size="sm" onClick={() => sendTest(it)} data-testid={`custom-test-${it.id}`}><Mail size={13} className="mr-1" />Test</Button>
                 <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`custom-edit-${it.id}`}><Pencil size={13} className="mr-1" />Modifier</Button>
                 <Button variant="ghost" size="sm" onClick={() => remove(it)} className="text-rose-600" data-testid={`custom-delete-${it.id}`}><Trash2 size={13} /></Button>
                 <Switch checked={it.active} onCheckedChange={(v) => toggleActive(it, v)} data-testid={`custom-switch-${it.id}`} />
               </div>
             </div>
           ))}
         </div>
       )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="custom-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier l'e-mail automatisé" : "Nouvel e-mail automatisé"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Nom (interne)</label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ex. Alerte admin — nouvelle carte" data-testid="custom-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Déclencheur</label>
              <Select value={form.trigger} onValueChange={(v) => set("trigger", v)}>
                <SelectTrigger data-testid="custom-trigger"><SelectValue placeholder="Choisir un événement" /></SelectTrigger>
                <SelectContent>
                  {triggers.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`custom-trigger-${t.key}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Destinataire</label>
              <Select value={form.recipient_type} onValueChange={(v) => set("recipient_type", v)}>
                <SelectTrigger data-testid="custom-recipient-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admins" data-testid="custom-recipient-admins">Tous les admins (sélection « Destinataires admin »)</SelectItem>
                  <SelectItem value="actor" data-testid="custom-recipient-actor">La personne concernée par l'événement</SelectItem>
                  <SelectItem value="custom" data-testid="custom-recipient-custom">Une adresse précise…</SelectItem>
                </SelectContent>
              </Select>
              {form.recipient_type === "custom" && (
                <Input className="mt-2" value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} placeholder="destinataire@exemple.fr" data-testid="custom-recipient-email" />
              )}
            </div>
            {currentTrigger && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                Variables disponibles :{" "}
                {currentTrigger.vars.map((v) => <code key={v} className="bg-white border border-slate-200 px-1 rounded mr-1">{`{${v}}`}</code>)}
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-slate-600">Objet</label>
              <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Ex. Nouvelle carte créée par {nom}" data-testid="custom-subject" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Message</label>
              <RichEditor key={editorKey} initialHtml={form.body} onChange={(v) => set("body", v)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="custom-active" /> Actif
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="custom-save">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Onglet « Destinataires admin »                                      */
/* ------------------------------------------------------------------ */
function AdminRecipients() {
  const [mode, setMode] = useState("all");
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emails/admin-recipients");
      setMode(data.mode || "all");
      setAdmins(data.all_admins || []);
      setSelected(new Set(data.selected || []));
    } catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (em) => setSelected((prev) => { const n = new Set(prev); n.has(em) ? n.delete(em) : n.add(em); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/emails/admin-recipients", { mode, emails: Array.from(selected) });
      toast.success("Destinataires admin enregistrés.");
    } catch { toast.error("Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm py-10">Chargement…</div>;

  return (
    <div data-testid="admin-emails-recipients" className="max-w-xl">
      <p className="text-slate-500 text-sm mb-4">
        Choisissez quels comptes admin reçoivent les <strong>notifications internes</strong> (nouvelles inscriptions, demandes, e-mails
        automatisés « admins », récap d'audience hebdomadaire…).
      </p>
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        <Select value={mode} onValueChange={setMode}>
          <SelectTrigger data-testid="recipients-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="recipients-mode-all">Tous les comptes admin</SelectItem>
            <SelectItem value="selected" data-testid="recipients-mode-selected">Seulement ceux que je choisis</SelectItem>
          </SelectContent>
        </Select>

        {mode === "selected" && (
          <div className="border border-slate-100 rounded-lg divide-y divide-slate-50" data-testid="recipients-list">
            {admins.length === 0 ? <div className="p-4 text-sm text-slate-400">Aucun compte admin.</div> :
             admins.map((a) => (
               <label key={a.email} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`recipients-item-${a.email}`}>
                 <Checkbox checked={selected.has(a.email)} onCheckedChange={() => toggle(a.email)} />
                 <span className="min-w-0 flex-1">
                   <span className="block text-sm text-slate-800 truncate">{a.name}</span>
                   <span className="block text-xs text-slate-400 truncate">{a.email} · {a.role}</span>
                 </span>
               </label>
             ))}
          </div>
        )}
        <Button onClick={save} disabled={saving} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="recipients-save">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Onglet « Envoi manuel »                                             */
/* ------------------------------------------------------------------ */
function ManualSend() {
  const [audience, setAudience] = useState("membre");
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [recips, setRecips] = useState([]);
  const [total, setTotal] = useState(0);
  const [audienceTotal, setAudienceTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [scope, setScope] = useState("selected");
  const [includeOptout, setIncludeOptout] = useState(false);
  const [optoutAvailable, setOptoutAvailable] = useState(0);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ audience, q, city, include_optout: includeOptout });
      const { data } = await api.get(`/admin/emails/recipients?${params.toString()}`);
      setRecips(data.items || []);
      setTotal(data.total || 0);
      setAudienceTotal(data.audience_total ?? data.total ?? 0);
      setOptoutAvailable(data.optout_available || 0);
      setTruncated(!!data.truncated);
      setCities(data.cities || []);
    } catch { toast.error("Erreur de chargement des destinataires."); }
    finally { setLoading(false); }
  }, [audience, q, city, includeOptout]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [audience, includeOptout]);

  const toggleOne = (em) => setSelected((prev) => { const n = new Set(prev); n.has(em) ? n.delete(em) : n.add(em); return n; });
  const allLoadedSelected = recips.length > 0 && recips.every((r) => selected.has(r.email));
  const toggleAll = () => setSelected((prev) => {
    if (allLoadedSelected) { const n = new Set(prev); recips.forEach((r) => n.delete(r.email)); return n; }
    const n = new Set(prev); recips.forEach((r) => n.add(r.email)); return n;
  });

  const recipientCount = scope === "selected" ? selected.size : scope === "all" ? audienceTotal : total;

  const doSend = async (test) => {
    if (!subject.trim() || subject.trim().length < 2) { toast.error("Ajoutez un objet."); return; }
    const plain = body.replace(/<[^>]+>/g, "").trim();
    if (plain.length < 3) { toast.error("Ajoutez un message."); return; }
    if (!test) {
      if (scope === "selected" && selected.size === 0) { toast.error("Sélectionnez au moins un destinataire."); return; }
      if (!window.confirm(`Envoyer cet e-mail à ${recipientCount} destinataire(s) ?`)) return;
    }
    setSending(true);
    try {
      const payload = { audience, subject: subject.trim(), body, scope, q, city, emails: scope === "selected" ? Array.from(selected) : [], include_optout: includeOptout, test: !!test };
      const { data } = await api.post("/admin/emails/send-manual", payload);
      if (test) toast.success(`E-mail de test envoyé à ${data.to}`);
      else toast.success(`Envoi lancé vers ${data.queued} destinataire(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-emails-manual" className="grid lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Users size={16} /> Destinataires</h3>
        <div className="space-y-2">
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger data-testid="manual-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MANUAL_AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`manual-audience-${a.value}`}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom ou e-mail)…" className="pl-8" data-testid="manual-search" />
          </div>
          <Select value={city || "__all__"} onValueChange={(v) => setCity(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="manual-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toutes les villes</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between mt-3 mb-1">
          <button onClick={toggleAll} className="text-xs font-semibold text-[#E05638] inline-flex items-center gap-1" data-testid="manual-select-all">
            <Checkbox checked={allLoadedSelected} className="mr-1" /> Tout sélectionner
          </button>
          <span className="text-xs text-slate-400">{total} trouvé(s){truncated ? " · 300 affichés" : ""}</span>
        </div>
        <div className="border border-slate-100 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-50" data-testid="manual-recipient-list">
          {loading ? <div className="p-4 text-sm text-slate-400">Chargement…</div> :
           recips.length === 0 ? <div className="p-4 text-sm text-slate-400">Aucun destinataire.</div> :
           recips.map((r) => (
             <label key={r.email} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`manual-recipient-${r.email}`}>
               <Checkbox checked={selected.has(r.email)} onCheckedChange={() => toggleOne(r.email)} />
               <span className="min-w-0 flex-1">
                 <span className="block text-sm text-slate-800 truncate">{r.name}</span>
                 <span className="block text-xs text-slate-400 truncate">{r.email}{r.city ? ` · ${r.city}` : ""}</span>
               </span>
             </label>
           ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="mt-2 text-slate-500" data-testid="manual-reload"><RefreshCw size={13} className="mr-1" /> Actualiser</Button>
      </div>

      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3"><Megaphone size={16} /> Composer l'e-mail</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Cible de l'envoi</label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="manual-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="selected" data-testid="manual-scope-selected">Destinataires cochés ({selected.size})</SelectItem>
                <SelectItem value="filtered" data-testid="manual-scope-filtered">Tous les résultats filtrés ({total})</SelectItem>
                <SelectItem value="all" data-testid="manual-scope-all">Tout le public sélectionné ({audienceTotal})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {optoutAvailable > 0 && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 cursor-pointer" data-testid="manual-include-optout-wrap">
              <Checkbox checked={includeOptout} onCheckedChange={(v) => setIncludeOptout(!!v)} data-testid="manual-include-optout" className="mt-0.5" />
              <span className="text-xs text-amber-800">Inclure aussi les <strong>{optoutAvailable}</strong> inscrit(s) désabonné(s) des alertes.<span className="block text-amber-600">À réserver aux communications importantes (RGPD).</span></span>
            </label>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-600">Objet</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l'e-mail…" data-testid="manual-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Message</label>
            <RichEditor onChange={setBody} />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" onClick={() => doSend(true)} disabled={sending} data-testid="manual-send-test"><Mail size={14} className="mr-1" /> M'envoyer un test</Button>
            <Button onClick={() => doSend(false)} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="manual-send"><Send size={14} className="mr-1" /> {sending ? "Envoi…" : `Envoyer (${recipientCount})`}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
const TABS = [
  { key: "auto", label: "E-mails automatiques", testid: "admin-emails-tab-auto" },
  { key: "custom", label: "E-mails personnalisés", testid: "admin-emails-tab-custom" },
  { key: "recipients", label: "Destinataires admin", testid: "admin-emails-tab-recipients" },
  { key: "manual", label: "Envoi manuel", testid: "admin-emails-tab-manual" },
];

export default function AdminEmails() {
  const [tab, setTab] = useState("auto");
  return (
    <div data-testid="admin-emails-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-emails-title">
          <Mail className="text-[#E05638]" size={26} /> E-mails &amp; Communications
        </h1>
        <p className="text-slate-500 text-xs mt-1">
          Visualisez et paramétrez tous les e-mails automatiques, créez des e-mails déclenchés par événement, et envoyez des e-mails ciblés.
        </p>
      </div>

      <div className="inline-flex flex-wrap rounded-xl bg-white border border-slate-200 p-1 gap-1 mb-5" data-testid="admin-emails-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={t.testid}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === t.key ? "bg-[#E05638] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {t.key === "custom" && <Zap size={14} />}{t.key === "recipients" && <UserCog size={14} />}{t.label}
          </button>
        ))}
      </div>

      {tab === "auto" && <AutoEmails />}
      {tab === "custom" && <CustomEmails />}
      {tab === "recipients" && <AdminRecipients />}
      {tab === "manual" && <ManualSend />}
    </div>
  );
}
